import $ from 'jquery';
import { Component } from '@mycolorway/tao';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import memberTodoSyncMixin from 'next/javascripts/mixins/member-todo-sync-mixin';
import resourceSubject from 'next/javascripts/reactivity/resource-subject';
import { closest } from 'next/javascripts/modules/dom';
import 'next/stylesheets/components/member-todo-box';

Component('tr-member-todo-box', {
  mixins: [eventListenerMixin, memberTodoSyncMixin],

  properties: {
    groupBy: {
      type: String,
    },

    sortBy: {
      type: String,
    },

    memberGuid: String,
  },

  ready() {
    this.listenTo(this, {
      'tr-todo-item-plus:running': this.handleTodoRunning.bind(this),
      'tr-todo-item-plus:pause': this.handleTodoPause.bind(this),
      'tr-todolist-plus:sortableMove': this.handleSortableMove.bind(this),
      'tr-todolist-plus:sortableEnd': this.handleSortableEnd.bind(this),
      'tr-todolist-plus:sortableChoose': this.handleSortableChoose.bind(this),
      'tr-todolist-plus:sortableUnchoose': this.handleSortableUnchoose.bind(this),
    });

    this.subscription = resourceSubject('todo').subscribe({
      next: (event, todo) => {
        if (!todo.assigneeGuid || todo.assigneeGuid !== this.memberGuid) return;

        if (event === 'create:pusher' || event === 'update:pusher') {
          if (!todo.isTouched) this.syncNewTodo(todo.guid);
        } else if (event === 'delete:pusher' && !todo.isActive) {
          this.removeTodoItems(todo.guid);
        }
      },
    });
  },

  syncNewTodo(todoGuid) {
    const todo = this.querySelectorAll(`tr-todo-item-plus[data-guid="${todoGuid}"]`);
    if (todo.length > 0) return;

    this.stopSyncNewTodo();

    const editing = !!this.querySelector('tr-todo-item-plus[editing]');
    if (editing) {
      this.newTodoSyncTimer = setTimeout(() => this.syncNewTodo(todoGuid), 1000);
    } else {
      $.getScript(window.location.href);
    }
  },

  stopSyncNewTodo() {
    if (this.newTodoSyncTimer) {
      clearTimeout(this.newTodoSyncTimer);
      this.newTodoSyncTimer = null;
    }
  },

  pauseRunningTodos({ except: todoGuid }) {
    Array.from(
      this.querySelectorAll(`tr-todo-item-plus.running:not([guid="${todoGuid}"])`),
    ).forEach(item => item.pause());
  },

  handleTodoRunning(event) {
    const todo = event.target;
    this.pauseRunningTodos({ except: todo.guid });

    if (todo.dataset.assigneeGuid === this.memberGuid) {
      if (todo.assignToOther) {
        todo.assignToOther = false;
      } else {
        Array.from(
          this.querySelectorAll(`tr-todo-item-plus[guid="${todo.guid}"]`),
        ).forEach(item => item.run());
      }
    } else {
      todo.assignToOther = true;
      this.updateTodoItems(todo.guid, todo);
    }
  },

  handleTodoPause(event) {
    Array.from(
      this.querySelectorAll(`tr-todo-item-plus[guid="${event.target.guid}"]`),
    ).forEach(item => item.pause());
  },

  async updateTodoItems(todoGuid, newTodoItem) {
    this.stopSyncTodo(todoGuid);

    const todoItems = Array.from(
      this.querySelectorAll(`tr-todo-item-plus[guid="${todoGuid}"]`),
    );

    if (todoItems.length > 0) {
      const removableParents = this.getRemovableParents(todoGuid);
      const replacementPromises = todoItems
        .map(item => item.replaceTodoItem($(newTodoItem).get(0)));

      await Promise.all(replacementPromises);
      const parentRemovePromises = removableParents
        .filter(parent => !parent.querySelector('.sub-todos tr-todo-item-plus'))
        .map(parent => parent.remove());
      await Promise.all(parentRemovePromises);
      this.refreshContainerStatus();
    }
  },

  refreshBoxesStatus() {
    const boxes = this.querySelectorAll('.box');
    boxes.forEach(box => this.refreshBoxStatus(box));
  },

  refreshBoxStatus(box) {
    const todos = $(box).find('tr-todolist-plus').children('tr-todo-item-plus');
    const todoCount = box.querySelector('.todo-count');

    todoCount.textContent = todos.length;
    box.classList.toggle('empty', !todos.length);
  },

  handleSortableMove(event) {
    [...this.querySelectorAll('.box')]
      .forEach(box => box.classList.remove('sorting'));

    const { originalEvent } = event.detail;
    closest(originalEvent.to, '.box').classList.add('sorting');
  },

  handleSortableEnd(event) {
    [...this.querySelectorAll('.box')]
      .forEach(box => box.classList.remove('sorting'));

    const { originalEvent } = event.detail;
    this.refreshBoxStatus(closest(originalEvent.to, '.box'));
    this.refreshBoxStatus(closest(originalEvent.from, '.box'));
  },

  handleSortableChoose() {
    this.style.paddingBottom = `${window.innerHeight}px`;
  },

  handleSortableUnchoose() {
    this.style.paddingBottom = '0px';
  },

  async removeTodoItems(todoGuid) {
    const todoItems = Array.from(
      this.querySelectorAll(`tr-todo-item-plus[guid="${todoGuid}"]`),
    );

    if (todoItems.length > 0) {
      const removableParents = this.getRemovableParents(todoGuid);
      const removalPromises = todoItems.map(item => item.removeSelfAndParent());

      await Promise.all(removalPromises);
      await Promise.all(removableParents.map(item => item.remove()));
      this.refreshContainerStatus();
    }
  },

  refreshContainerStatus() {
    if (this.sortBy === 'sequence') {
      this.refreshBoxesStatus();
    } else {
      this.refreshGroupStatus();
    }
  },

  getRemovableParents(childTodoGuid) {
    const parentTodoItems = Array.from(
      this.querySelectorAll(`tr-todo-item-plus[guid="${childTodoGuid}"]`),
    ).map(item => $(item).parents('tr-todo-item-plus').get(0));

    return parentTodoItems.filter((todoItem) => {
      if (!todoItem) return false;

      const todoItems = this
        .querySelectorAll(`tr-todo-item-plus[guid="${todoItem.guid}"]`);
      const hasSubTodos = todoItem.querySelectorAll(
        `.sub-todos tr-todo-item-plus:not([guid="${childTodoGuid}"])`,
      ).length > 0;

      return !hasSubTodos && (
        todoItems.length > 1 || (todoItems.length === 1 && todoItems[0].assignToOther)
      );
    });
  },

  refreshGroupStatus() {
    this.classList.toggle('empty', !this.querySelector('tr-todo-item-plus'));

    if (this.groupBy !== 'nothing') {
      [...this.querySelectorAll('tr-todos-group')].forEach((group) => {
        const rootTodoItems = Array.from(
          group.querySelectorAll('tr-todolist-plus > tr-todo-item-plus'),
        );

        if (rootTodoItems.length > 0) {
          // eslint-disable-next-line no-param-reassign
          group.count = rootTodoItems.length;
        } else {
          group.parentNode.removeChild(group);
        }
      });
    }
  },

  disconnected() {
    this.stopSyncNewTodo();
    this.stopListening();

    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  },
});
